/**
 * In-App Sleep Tools: Audio Engine
 * Supports Native Background Audio on iOS & Android via @capgo/capacitor-native-audio,
 * allowing uninterrupted playback when the screen locks or the app is minimized.
 * Preserves browser Web Audio API synthesis as a fallback for web/desktop.
 */

import { Capacitor } from '@capacitor/core';
import { NativeAudio } from '@capgo/capacitor-native-audio';

export type SoundType = 'white' | 'pink' | 'brown' | 'womb';

const soundLabels: Record<SoundType, string> = {
  white: 'White Noise',
  pink: 'Pink Noise',
  brown: 'Brown Noise',
  womb: 'Womb Rhythm',
};

class AudioSynthesizer {
  private isNative: boolean = false;
  private currentSound: SoundType | null = null;
  private currentVolume: number = 0.6;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private intervalTimerId: ReturnType<typeof setInterval> | null = null;
  private remainingSeconds: number = 0;
  private onTimeUpdateCallback: ((seconds: number) => void) | null = null;

  // Web Audio state
  private audioCtx: AudioContext | null = null;
  private gainNode: GainNode | null = null;

  // Native Audio state
  private preloadedAssets = new Set<string>();
  private nativeConfigured = false;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  private async configureNative() {
    if (!this.isNative || this.nativeConfigured) return;
    try {
      await NativeAudio.configure({
        backgroundPlayback: true,
        focus: true,
        showNotification: true,
      });
      this.nativeConfigured = true;
    } catch (e) {
      console.warn('NativeAudio configure error:', e);
    }
  }

  private async preloadNativeAsset(type: SoundType): Promise<boolean> {
    if (!this.isNative) return false;
    await this.configureNative();

    if (this.preloadedAssets.has(type)) {
      return true;
    }

    try {
      const isAlready = await NativeAudio.isPreloaded({
        assetId: type,
        assetPath: `sounds/${type}.wav`,
      }).catch(() => ({ found: false }));
      if (isAlready && isAlready.found) {
        this.preloadedAssets.add(type);
        return true;
      }

      await NativeAudio.preload({
        assetId: type,
        assetPath: `sounds/${type}.wav`,
        audioChannelNum: 1,
        volume: this.currentVolume,
        notificationMetadata: {
          title: soundLabels[type] || 'Sleep Sound',
          artist: 'LullabAI Sleep Tools',
          album: 'LullabAI Nursery',
        },
      });
      this.preloadedAssets.add(type);
      return true;
    } catch (e) {
      console.warn(`Failed to preload native audio asset ${type}:`, e);
      return false;
    }
  }

  private initAudioContext(): AudioContext {
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new AudioContextClass();
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
    return this.audioCtx;
  }

  public isPlaying(): boolean {
    return this.currentSound !== null;
  }

  public getCurrentSound(): SoundType | null {
    return this.currentSound;
  }

  public getRemainingSeconds(): number {
    return this.remainingSeconds;
  }

  public setOnTimeUpdate(cb: ((seconds: number) => void) | null) {
    this.onTimeUpdateCallback = cb;
  }

  /**
   * Starts playing or synthesizing the chosen sound.
   * @param type Sound preset
   * @param initialVolume 0.0 to 1.0
   * @param durationMinutes 0 for continuous
   */
  public async start(type: SoundType, initialVolume: number = 0.5, durationMinutes: number = 0) {
    this.currentVolume = Math.max(0.01, Math.min(1.0, initialVolume));

    if (this.isNative) {
      await this.startNative(type, this.currentVolume);
    } else {
      this.startWebAudio(type, this.currentVolume);
    }

    this.setupTimer(durationMinutes);
  }

  private async startNative(type: SoundType, volume: number) {
    const prevSound = this.currentSound;
    if (prevSound && prevSound !== type) {
      try {
        await NativeAudio.stop({ assetId: prevSound, fadeOut: true, fadeOutDuration: 0.3 });
      } catch {
        // Ignore errors stopping previous audio
      }
    }

    const preloaded = await this.preloadNativeAsset(type);
    if (!preloaded) {
      console.warn('Native preload failed, falling back to Web Audio');
      this.startWebAudio(type, volume);
      return;
    }

    try {
      await NativeAudio.loop({ assetId: type });
      await NativeAudio.setVolume({ assetId: type, volume });
      this.currentSound = type;
    } catch (e) {
      console.warn('Error looping native audio, falling back to Web Audio:', e);
      this.startWebAudio(type, volume);
    }
  }

  private startWebAudio(type: SoundType, volume: number) {
    this.stopWebAudio();

    const ctx = this.initAudioContext();
    this.gainNode = ctx.createGain();
    this.gainNode.gain.setValueAtTime(0.001, ctx.currentTime);
    this.gainNode.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + 0.1);
    this.gainNode.connect(ctx.destination);

    const bufferSize = ctx.sampleRate * 2; // 2 seconds looped buffer
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    if (type === 'white') {
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
    } else if (type === 'pink') {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }
    } else if (type === 'brown') {
      let lastOut = 0.0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (lastOut + 0.02 * white) / 1.02;
        lastOut = data[i];
        data[i] *= 3.5;
      }
    } else if (type === 'womb') {
      let lastOut = 0.0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        lastOut = (lastOut + 0.015 * white) / 1.015;
        const t = i / ctx.sampleRate;
        const pulse = Math.pow(Math.sin(2 * Math.PI * 1.1 * t), 4) * 0.5 + 0.5;
        data[i] = lastOut * 4.0 * pulse;
      }
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    if (type === 'brown' || type === 'womb') {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(type === 'womb' ? 350 : 600, ctx.currentTime);
      source.connect(filter);
      filter.connect(this.gainNode);
    } else {
      source.connect(this.gainNode);
    }

    source.start(0);
    this.currentSound = type;
  }

  private setupTimer(durationMinutes: number) {
    if (this.intervalTimerId) {
      clearInterval(this.intervalTimerId);
      this.intervalTimerId = null;
    }
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    if (durationMinutes > 0) {
      this.remainingSeconds = durationMinutes * 60;
      if (this.onTimeUpdateCallback) {
        this.onTimeUpdateCallback(this.remainingSeconds);
      }

      this.intervalTimerId = setInterval(() => {
        this.remainingSeconds -= 1;
        if (this.onTimeUpdateCallback) {
          this.onTimeUpdateCallback(this.remainingSeconds);
        }
        if (this.remainingSeconds <= 0) {
          this.fadeAndStop(3);
        }
      }, 1000);
    } else {
      this.remainingSeconds = 0;
      if (this.onTimeUpdateCallback) {
        this.onTimeUpdateCallback(0);
      }
    }
  }

  /**
   * Smoothly adjusts volume.
   */
  public async setVolume(volume: number) {
    const clamped = Math.max(0.01, Math.min(1.0, volume));
    this.currentVolume = clamped;

    if (this.isNative && this.currentSound) {
      try {
        await NativeAudio.setVolume({ assetId: this.currentSound, volume: clamped });
      } catch (e) {
        console.warn('Error setting native volume:', e);
      }
    }

    if (this.gainNode && this.audioCtx) {
      try {
        this.gainNode.gain.cancelScheduledValues(this.audioCtx.currentTime);
        this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, this.audioCtx.currentTime);
        this.gainNode.gain.exponentialRampToValueAtTime(clamped, this.audioCtx.currentTime + 0.05);
      } catch {
        // Ignore audio parameter scheduling errors
      }
    }
  }

  /**
   * Smoothly fades out and stops audio.
   */
  public async fadeAndStop(fadeDuration: number = 0.5) {
    const soundToStop = this.currentSound;

    if (this.isNative && soundToStop) {
      try {
        await NativeAudio.stop({
          assetId: soundToStop,
          fadeOut: true,
          fadeOutDuration: fadeDuration,
        });
      } catch (e) {
        console.warn('Error stopping native audio:', e);
      }
    }

    if (this.gainNode && this.audioCtx && this.audioCtx.state === 'running') {
      try {
        this.gainNode.gain.cancelScheduledValues(this.audioCtx.currentTime);
        this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, this.audioCtx.currentTime);
        this.gainNode.gain.exponentialRampToValueAtTime(0.0001, this.audioCtx.currentTime + fadeDuration);
        setTimeout(() => this.stop(), fadeDuration * 1000);
        return;
      } catch (e) {
        console.warn('Error during audio fade-out', e);
      }
    }

    this.stop();
  }

  private stopWebAudio() {
    if (this.audioCtx) {
      try {
        if (this.audioCtx.state !== 'closed') {
          this.audioCtx.close().catch(() => {});
        }
      } catch {
        // Ignore errors closing audio context
      }
      this.audioCtx = null;
    }
    this.gainNode = null;
  }

  /**
   * Immediately stops playback and clears timers.
   */
  public async stop() {
    if (this.intervalTimerId) {
      clearInterval(this.intervalTimerId);
      this.intervalTimerId = null;
    }
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.remainingSeconds = 0;
    if (this.onTimeUpdateCallback) {
      this.onTimeUpdateCallback(0);
    }

    const soundToStop = this.currentSound;
    this.currentSound = null;

    if (this.isNative && soundToStop) {
      try {
        await NativeAudio.stop({ assetId: soundToStop });
      } catch {
        // Ignore errors stopping native audio
      }
    }

    this.stopWebAudio();
  }
}

export const audioSynthesizer = new AudioSynthesizer();
